/**
 * Таблица «маршрут → источник данных».
 *
 * Одно место, где сказано, чем питается каждый экран продукта. Пока источник
 * объявлен здесь, а не внутри экрана, три вещи держатся сами:
 *
 * 1. новый маршрут без объявленного источника роняет тест (`routes.test.ts`);
 * 2. экран на фикстурах печатает это человеку — общим компонентом
 *    (`FixtureNotice`), а не доброй волей автора экрана;
 * 3. перевод экрана на канон — правка ОДНОЙ строки здесь плюс реализация
 *    источника; сам экран не трогается.
 *
 * `reason` различает два очень разных положения. `pending-api` — эндпоинт в
 * каноне ЕСТЬ, экран переводится на него названной задачей. `no-canonical-source`
 * — источника нет вообще, и экран живёт на фикстурах до решения владельца
 * (задача 1.9 плана `astra-on-canon`). Смешивать их нельзя: первое — график
 * работ, второе — дырка в модели.
 */

import type { RouteSource } from './types';

/**
 * Маршруты без собственных данных. Список именно список: он существует, чтобы
 * «экрана данных нет» нельзя было сказать про любой маршрут молча.
 */
export const NO_DATA_ROUTES: readonly string[] = ['*'];

const PENDING = (note: string): Pick<RouteSource, 'source' | 'reason' | 'note'> => ({
  source: 'fixtures',
  reason: 'pending-api',
  note,
});

const NO_SOURCE = (note: string): Pick<RouteSource, 'source' | 'reason' | 'note'> => ({
  source: 'fixtures',
  reason: 'no-canonical-source',
  note,
});

const API = (note: string): Pick<RouteSource, 'source' | 'note'> => ({
  source: 'api',
  note,
});

export const ROUTE_SOURCES: readonly RouteSource[] = [
  /* --- вход ------------------------------------------------------------- */
  { path: '/reports/:ref', ...API('сохранённый отчёт ИИ — /ai/agent/saved-reports (автор) и /ai/agent/public/reports/{token} (без входа)') },
  { path: '/login', ...API('вход и «кто вошёл» — /auth/login и /auth/me') },

  /* --- кабинет решений и сезон ------------------------------------------ */
  { path: '/', ...API('сводка района и очередь ситуаций — /season/picture и /situations') },
  { path: '/season', ...API('прежняя «Картина сезона» — /season/picture и /situations (откат и прямые ссылки)') },
  { path: '/district', ...API('обзор района — /season/picture, /water/summary, /quarantine/statuses и /assignments') },
  // i18n-ignore: служебная заметка об источнике для реестра ИИ, не текст интерфейса
  { path: '/guide', ...API('обучение идёт по тем же данным, что и экраны') },
  // Внимание района показано в форме сервера: вида (`kind`) и даты (`since`)
  // у пункта нет, и экран говорит это словами, а не досочиняет.
  {
    path: '/overview',
    ...API('обзор района — /overview/district и картина активного цикла /season/picture'),
  },
  { path: '/cycles', ...API('районные циклы — /season/cycles') },
  { path: '/cycles/:id', ...API('цикл и его работы — /season/cycles и /season/picture') },
  { path: '/cycles/:id/works/:workId', ...API('работа цикла — /season/picture') },
  { path: '/templates/:id', ...API('шаблон агроцикла — /agrocycle/templates/{id}') },
  // Карта-станция — на каноне: полигоны, контуры, массивы, хозяйства, циклы,
  // культура сезона (размещение демо-слоя с фичей карты) и карантин
  // (`/quarantine/statuses`). Слой «Статус работы» честно отказывает словами,
  // а не красит район «по умолчанию»: состояния обязательств ПО КОНТУРАМ канон
  // отдаёт только по одному обязательству хозяйства, районного набора нет.
  {
    path: '/map',
    ...API(
      'карта-станция — полотно MapCanvas на /map/reference и /map/contours, атрибуты — реестр ' +
        'контуров и циклы, карантин — /quarantine/statuses; слой «Статус работы» на этом экране пока не подключён',
    ),
  },

  /* --- реестры территории ------------------------------------------------ */
  { path: '/farms', ...API('хозяйства — /actors и реестр контуров канона') },
  {
    // РАЗРЫВ, названный прямо. Ход циклов и вода карточки приходят из канона,
    // а блок «Внеплановые работы» читает наборы `extra-works-<цикл>.json`, за
    // которые не взялся ни один адаптер: на api-источнике он честно уходит в
    // отказ («набор не подключён»), но пустым нулём не притворяется. Больше
    // того, имена циклов внутри `pages/my/extraWorks.ts` — зашитая пара
    // прототипа, которой в каноне нет; адаптер придётся заводить вместе с
    // переходом на ключ цикла канона (пара «сезон × культура»).
    path: '/farms/:id',
    ...API(
      'хозяйство — /actors, ход циклов из /season/picture?actor_id= и баланс воды того же среза; ' +
        'внеплановые работы хозяйства канонического источника пока не имеют и на экране отказывают',
    ),
  },
  {
    path: '/massifs/:id',
    ...API('массив — /massifs и ход циклов из /season/picture?massif_id='),
  },
  { path: '/contours', ...API('реестр контуров — /contours') },
  {
    path: '/contours/:id',
    ...API(
      'контур — /contours/{id} (землепользования, карантин) и работы контура из /verification/queue?contour_id=',
    ),
  },
  {
    path: '/quarantine',
    ...API('карантин — статусы контуров /quarantine/statuses; районного журнала обследований канон не отдаёт'),
  },

  /* --- вода -------------------------------------------------------------- */
  // Вода — события файла ирригации (agroai-0e8): полив закрывает событие, а не
  // заявка. Журнал заявок (`/water/ledger`) — запасная ветка, когда событий нет;
  // разбор по поливу идёт ответом `/water/works/{work_def_id}`.
  {
    path: '/water',
    ...API('ирригация — /water/balance, поливы цикла /water/works/{id}, заявки /water/ledger'),
  },
  {
    path: '/analysis/water',
    ...API('подробно о воде цикла по файлу ирригации — /water/detail (поливы, каналы → хозяйства, земля, без подачи, недели, вне плана)'),
  },

  /* --- разбор и отчёты --------------------------------------------------- */
  { path: '/analytics', ...API('аналитик — инструменты /ai/answer, /ai/plan, /ai/answers') },
  { path: '/explore', ...PENDING('разбор ждёт свободной выборки поверх /ai/*') },
  // Сравнение — оси рядом, без балла и рейтинга: строки by_farm картины
  // сезона и земля из /actors/{id}.
  { path: '/compare', ...API('сравнение хозяйств — by_farm /season/picture и /actors/{id}') },
  { path: '/reports/briefing', ...API('сводка для совещания — /season/picture и /situations') },
  {
    path: '/reports/pakhta-galla',
    ...NO_SOURCE('результаты сезона (урожай и затраты) в базе района не хранятся'),
  },

  /* --- без канонического источника (задача 1.9) -------------------------- */
  // Планирование ушло на канон (agroai-b48): площадь, окно и вода из базы района.
  { path: '/equipment', ...NO_SOURCE('техники как сущности в базе района нет') },
  // Планирование — калькулятор: площадь, окно работы и подача воды идут из канона,
  // а техника, нормы и бюджет — допущения пользователя в браузере («черновик»).
  {
    path: '/planning',
    // i18n-ignore: служебная заметка об источнике для реестра ИИ, не текст интерфейса
    ...API('планирование ресурсов — земля и план агроцикла /agrocycles, подача воды /water/district; техника, нормы и бюджет — допущения пользователя в браузере (черновик)'),
  },
  {
    path: '/ops/campaigns',
    ...API('кампании менеджера — /campaign-desk, запуск и решения по кампании (ядро агроцикла S5), «Скоро» — /agrocycles/upcoming'),
  },

  /* --- кабинет хозяйства -------------------------------------------------- */
  { path: '/my/today', ...API('что ждут от хозяйства — /my/works и /season/cycles') },
  { path: '/my/fields', ...API('поля хозяйства — /actors/{id}, землепользование и размещения') },
  {
    path: '/my/report/:cycleId/:workId',
    ...API('отчёт хозяйства — /farm-obligations/{id} и запись /obligations/{id}/report'),
  },
  { path: '/my/history', ...API('отчёты и суждения — /farm-obligations/{id}') },

  /* --- кабинет управления ------------------------------------------------- */
  { path: '/ops/verify', ...API('очередь проверки — /verification/queue и запись /obligations/{id}/verify') },
  { path: '/ops/entry', ...API('ввод за хозяйство — /farm-obligations/{id}/report') },
  { path: '/ops/templates', ...API('конструктор шаблона — /agrocycle/live-templates, /agrocycle/templates/{id}/live, /changes; правки /stages и /works; /agrocycle/catalog') },
  { path: '/ops/water', ...API('загрузка файла ирригации — /water/reports, версии /water/report-versions, разбор /water/review и /water/events/{id}, споры /water/disputes') },
  { path: '/ops/placement', ...API('размещение — /placements, импорт /placements/import, ошибки данных /placements/conflicts') },
  { path: '/ops/quarantine', ...API('ввод карантина — /quarantine/statuses, запись /quarantine/inspections|findings/{id}/requirements|decisions|referrals') },
  {
    path: '/ops/assignments',
    ...API('поручения — /assignments, запись /assignments/issue и /assignments/{id}/start|report|check, «что изменилось» /assignments/{id}/diff'),
  },

  /* --- раздел «Данные»: канонические экраны ------------------------------- */
  { path: '/registry', ...API('что вошло в канон и что нет — /registry/summary') },
  { path: '/data-quality', ...API('замечания сборки — /data-quality/issues') },
  { path: '/data-map', ...API('канонический MapCanvas — /map/*') },

  /* --- канонические экраны вне навигации (адрес сохранён) ---------------- */
  { path: '/actors', ...API('реестр хозяйств каноники — /actors') },
  { path: '/actors/:actorId', ...API('хозяйство каноники — /actors/{id}') },
  { path: '/agrocycle', ...API('агроцикл каноники — /agrocycle/*') },
  { path: '/campaigns', ...API('кампании каноники — /campaigns') },
  { path: '/situations', ...API('ситуации каноники — /situations') },
  { path: '/verification', ...API('очередь проверки каноники — /verification/queue') },
  { path: '/my-works', ...API('ввод за хозяйство каноники — /my/works') },
  { path: '/works/:workId', ...API('работа каноники — /works/{id}') },
];

const BY_PATH = new Map(ROUTE_SOURCES.map((row) => [row.path, row]));

/** Объявление источника для шаблона пути; `null` — не объявлено. */
export function routeSource(path: string): RouteSource | null {
  return BY_PATH.get(path) ?? null;
}
